import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h6, link, paragraphLarge } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import ProductCardStyles from '@stories/Components/Cards/ProductCard/ProductCard.styles';
import SavingsProductFiltersStyles from '@stories/Components/Listings/SavingsProductFilters/SavingsProductFilters.styles';
import SortByDropdownStyles from '@stories/Components/Listings/SortByDropdown/SortByDropdown.styles';
import FormStyles from '@stories/Components/Misc/Forms/Form.styles';
import CheckboxListStyles from '@stories/Components/Misc/Forms/Inputs/CheckboxList/CheckboxList.styles';
import RadioListStyles from '@stories/Components/Misc/Forms/Inputs/RadioList/RadioList.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import NavigationStyles from '../Global/Navigation/Navigation.styles';
const cardGridStyles = css `
  grid-column: 1 / -1;

  @media ${from(Device.DesktopSmall)} {
    grid-column: 4 / span 9;
  }
`;
const Container = styled(SectionWrapper) `
  ${SectionWrapperStyles.Inner} {
    ${baseGrid};

    row-gap: ${fluid(24, 26)};
  }

  ${SortByDropdownStyles.Container} {
    grid-column: 7 / span 6;
    margin-bottom: ${fluid(0, 18)};

    @media ${from(Device.Tablet)} {
      grid-column: 9 / span 4;
    }

    @media ${from(Device.DesktopSmall)} {
      grid-column: 10 / span 3;
    }

    @media ${from(Device.Desktop)} {
      grid-column: 11 / span 2;
    }
  }

  ${ButtonStyles.Container}[data-filter-btn] {
    --buttonHeight: ${fluid(48, 55)};
    --textColour: ${brand.global.black};
    --borderColour: ${brand.blue.navy};
    --iconTextColour: currentColor;

    grid-column: 1 / span 6;
    margin-top: auto;
    border-radius: 10px;
    justify-content: space-between;

    @media ${from(Device.Tablet)} {
      grid-column: 1 / span 3;
    }

    @media ${from(Device.DesktopSmall)} {
      display: none;
    }

    ${ButtonStyles.Text} {
      font-size: 14px;
      font-weight: 600;
    }

    ${ButtonStyles.IconWrapper} {
      ${transition('transform', 0.2)};

      --iconHeight: 12px;
      --padding: 0px;

      transform: rotate(-90deg);
      background-color: var(--contrastColour);
    }

    &:hover ${ButtonStyles.IconWrapper} {
      background-color: var(--primaryColour);
      transform: translateX(4px) rotate(-90deg);
    }
  }

  ${ProductCardStyles.Container} {
    ${cardGridStyles};
  }
`;
const FilterTray = styled.div `
  @media ${until(Device.DesktopSmall)} {
    ${transition('transform', 0.5)};

    --trayButtonsHeight: 64px;
    --maxTrayWidth: 480px;

    position: fixed;
    top: var(--headerHeight, 0px);
    right: 0;
    z-index: ${Layers.Modal};
    overflow: auto;
    height: calc(100dvh - var(--headerHeight, 0px));
    width: 100%;
    background-color: ${brand.global.white};
    padding: 18px var(--sitePadding) 0;
    max-width: var(--maxTrayWidth);
    display: flex;
    flex-direction: column;

    &[data-open='false'] {
      transform: translateX(100%);
    }

    ${SavingsProductFiltersStyles.Container} {
      height: 100%;
    }

    ${SavingsProductFiltersStyles.ButtonWrapper} {
      background-color: ${brand.global.white};
      align-items: center;
      justify-content: flex-end;
      border-top: 1px solid ${brand.blue.navy};
      padding: 12px 16px;
      width: calc(100% + var(--sitePadding) * 2);
      margin: auto calc(var(--sitePadding) * -1) 0;
      position: sticky;
      bottom: 0;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    position: sticky;
    top: calc(var(--headerHeight, 0px) + ${fluid(16, 24)});
    grid-column: 1 / span 3;
    grid-row: 1 / span 3;
  }

  ${FormStyles.Title} {
    ${srOnly};
  }
`;
const FilterTrayBackdrop = styled(NavigationStyles.Backdrop) `
  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const FiltersInner = styled.div `
  container-type: inline-size;

  @media ${until(Device.DesktopSmall)} {
    height: 100%;
    width: 100%;
  }

  @media ${from(Device.DesktopSmall)} {
    position: sticky;
    top: 0;
    padding: 24px 16px 18px;
    grid-column: 1 / span 3;
    grid-row: 1 / span all;
    box-shadow: 0px -3px 20.4px 0px ${rgba(brand.global.black, 0.1)};
    border-radius: 20px;

    ${CheckboxListStyles.Container}:not(:has(${CheckboxListStyles.Option}:nth-child(3))) ${CheckboxListStyles.Option},
    ${RadioListStyles.Container}:not(:has(${RadioListStyles.Option}:nth-child(3))) ${RadioListStyles.Option} {
      flex: 0;
    }
  }
`;
const BackButton = styled.a `
  ${transition('text-decoration-color')};
  ${h6};
  ${link};

  display: flex;
  gap: ${fluid(18, 22)};
  border-radius: 20px 20px 0 0;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  background-color: ${brand.grey.light};
  padding: 14px 16px 12px;
  margin: -24px -16px 24px;

  &:hover,
  &:focus-visible {
    text-decoration-color: currentColor;
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const ButtonIcon = styled.span `
  ${transition('transform', 0.2)};

  --bgColour: ${brand.blue.default};

  display: grid;
  place-items: center;
  color: ${brand.global.white};
  background-color: var(--bgColour);
  height: 28px;
  width: 28px;
  border-radius: 5000px;

  svg {
    width: 16px;
    transform: scale(-1);
  }

  ${BackButton}:hover & {
    transform: translateX(-4px);
  }
`;
const Pagination = styled.div `
  grid-column: 1 / -1;
  padding-top: ${fluid(0, 28)};
  display: flex;
  justify-content: center;
`;
const NoResultsText = styled.p `
  ${cardGridStyles};
  ${paragraphLarge};

  text-align: center;
`;
const ProductListingSavingsStyles = {
    BackButton,
    ButtonIcon,
    Container,
    FiltersInner,
    FilterTray,
    FilterTrayBackdrop,
    NoResultsText,
    Pagination,
};
export default ProductListingSavingsStyles;
